import { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useModalDisplayedEvent } from '@zenbusiness/application-commons-clickstream';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';

import AuthModal from './AuthModal';
import * as FormStyled from './AuthModal.form.style';
import { AUTH_VALIDATIONS } from './constants/validations';

/**
 * Forgot Password Modal
 *
 * Two-step flow:
 * 1. Enter email to receive reset link
 * 2. Confirmation message
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @returns {object} The ForgotPasswordModal component
 */
function ForgotPasswordModal({ isOpen, onClose }) {
  const [step, setStep] = useState('request'); // 'request' | 'success'
  const [globalError, setGlobalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const { resetPassword } = useAuthentication();
  const captureException = useCaptureException();

  // Track modal display
  useModalDisplayedEvent({
    open: isOpen,
    elementName: 'forgot_password_modal'
  });

  // Initialize form
  const formMethods = useForm({
    defaultValues: {
      email: ''
    },
    mode: 'onBlur'
  });

  const { handleSubmit, register, formState } = formMethods;
  const { errors } = formState;

  /**
   * Handle form submission
   */
  const onSubmit = useCallback(
    async (data) => {
      setIsSubmitting(true);
      setGlobalError(null);

      try {
        await resetPassword(data.email);

        // Success! Move to confirmation step
        setSubmittedEmail(data.email);
        setStep('success');
      } catch (error) {
        captureException(error, {
          level: 'warning',
          context: { action: 'forgot_password' }
        });

        // Even if there's an error, show success to prevent email enumeration
        // This is a security best practice
        setSubmittedEmail(data.email);
        setStep('success');
      } finally {
        setIsSubmitting(false);
      }
    },
    [resetPassword, captureException]
  );

  /**
   * Handle form submission errors
   */
  const onSubmitError = useCallback((formErrors) => {
    console.error('Form validation errors:', formErrors);
    setGlobalError('Please fix the errors above and try again.');
  }, []);

  /**
   * Handle close and reset state
   */
  const handleClose = useCallback(() => {
    // Reset to initial state
    setStep('request');
    setGlobalError(null);
    setSubmittedEmail('');
    formMethods.reset();
    onClose();
  }, [onClose, formMethods]);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <AuthModal isOpen={isOpen} onClose={handleClose}>
      {step === 'request' ? (
        <FormProvider {...formMethods}>
          <FormStyled.FormRoot
            component="form"
            noValidate
            onSubmit={handleSubmit(onSubmit, onSubmitError)}
          >
            <FormStyled.FormStack>
              {/* Global Error */}
              {globalError && (
                <Box pb={2}>
                  <Text color="red" size="sm">
                    {globalError}
                  </Text>
                </Box>
              )}

              {/* Title Section */}
              <FormStyled.TitleContainer>
                <FormStyled.Title size="lg" weight="bold">
                  Forgot your password?
                </FormStyled.Title>
                <FormStyled.Subtitle>
                  Enter your email address and we&apos;ll send you instructions to reset your
                  password.
                </FormStyled.Subtitle>
              </FormStyled.TitleContainer>

              {/* Email Field */}
              <FormStyled.FieldStack>
                <TextField
                  {...register('email', AUTH_VALIDATIONS.email)}
                  autoComplete="email"
                  autoFocus
                  disabled={isSubmitting}
                  error={Boolean(errors.email)}
                  fullWidth
                  helperText={errors.email?.message}
                  label="Email"
                  required
                  type="email"
                />
              </FormStyled.FieldStack>

              {/* Buttons */}
              <FormStyled.ButtonStack>
                <FormStyled.SubmitButton
                  disabled={hasErrors || isSubmitting}
                  loading={isSubmitting}
                  type="submit"
                >
                  {isSubmitting ? 'Sending...' : 'Reset password'}
                </FormStyled.SubmitButton>

                <FormStyled.SecondaryButton variant="outlined" onClick={handleClose}>
                  Close
                </FormStyled.SecondaryButton>
              </FormStyled.ButtonStack>
            </FormStyled.FormStack>
          </FormStyled.FormRoot>
        </FormProvider>
      ) : (
        <FormStyled.FormStack>
          <FormStyled.TitleContainer>
            <FormStyled.Title size="lg" weight="bold">
              Email sent to {submittedEmail}
            </FormStyled.Title>
            <FormStyled.Subtitle>Check your inbox for a password reset link.</FormStyled.Subtitle>
          </FormStyled.TitleContainer>

          {/* Buttons */}
          <FormStyled.ButtonStack>
            <FormStyled.SecondaryButton fullWidth size="large" variant="outlined" onClick={onClose}>
              Close
            </FormStyled.SecondaryButton>
          </FormStyled.ButtonStack>
        </FormStyled.FormStack>
      )}
    </AuthModal>
  );
}

ForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ForgotPasswordModal;
